/**
 * Blog post language-resolution logic.
 *
 * A blog post is resolved by slug (slugs are unique per language) and always
 * rendered in its OWN language. The only question is what to do when the UI
 * language differs from the article's language:
 *
 * - On a COLD load / direct navigation (e.g. a visitor or crawler landing on an
 *   English URL while the UI defaults to Norwegian) we must adopt the article's
 *   language — NOT redirect to the other language's version. Redirecting here is
 *   the bug that made English posts serve Norwegian content to Google.
 * - Only once the UI language has matched the article (`synced`) is a later
 *   mismatch treated as a DELIBERATE switch (toggle / end-of-article button),
 *   which navigates to the equivalent post in the chosen language.
 */
export type LangAction = 'sync' | 'adopt' | 'navigate' | 'none';

export function nextLanguageAction(args: {
  uiLang: 'no' | 'en';
  postLang: 'no' | 'en';
  /** true once the UI language has matched this article at least once */
  synced: boolean;
  /** whether an equivalent post exists in the UI language */
  hasCounterpart: boolean;
}): LangAction {
  const { uiLang, postLang, synced, hasCounterpart } = args;
  // UI already matches the article → nothing to do; mark as synced.
  if (uiLang === postLang) return 'sync';
  // Cold load / fresh navigation → show the article in its own language.
  if (!synced) return 'adopt';
  // Deliberate switch → go to the equivalent post, if one exists.
  return hasCounterpart ? 'navigate' : 'none';
}
